import $ from "cash-dom";
import { request } from "@common/request";

/**
 * "Show more coupons" on the merchant / category page.
 *
 * The generated file carries the first 80 coupons; a busy category has ~2,000
 * (see CategoryPageService — asked without a limit the gateway answers with a
 * 7.85 MB payload). This fetches the next window when the visitor asks for it.
 *
 * THE CARDS COME BACK AS HTML, NOT JSON. `GET /api/store/coupons` renders them
 * from the same `shared/_coupon-box.ejs` the generator uses, so an appended
 * card is identical to a written one and there is no second copy of that markup
 * living in JavaScript to drift out of sync.
 *
 * OUR OWN ORIGIN, NOT THE GATEWAY. Every other call in this bundle goes
 * straight to the gateway; this one cannot, because it needs a server that can
 * render an EJS partial. goGlobal.WEB_URL is that origin.
 *
 * The counters live on the wrapper — data-total / data-loaded / data-next-offset
 * — because the page is a static file: there is nowhere else to keep them, and
 * they are what the next click reads.
 */

const PARENT = 'div[data-type="coupon-box-parent"]';
const BUTTON = '[data-type="show-more"]';

/** Bumped per filter fetch; only the newest response is applied. */
let filterRequestToken = 0;

/** window.goGlobal.WEB_URL, or this page's own origin as a fallback. */
function webOrigin() {
  const configured = String((window.goGlobal || {}).WEB_URL || "").replace(
    /\/+$/,
    "",
  );
  return configured || window.location.origin;
}

function toInt(value) {
  const parsed = parseInt(value, 10);
  return Number.isFinite(parsed) ? parsed : 0;
}

/**
 * What the visitor has selected, read straight off the DOM.
 *
 * Read rather than remembered: the ported .in handlers already own these
 * controls (they tick the box, add the chip, flip the toggle's data-state), so
 * keeping a parallel copy in this module would be a second source of truth to
 * get out of step.
 */
export function currentFilterState() {
  const ids = [];
  document
    .querySelectorAll(
      'div[data-type="filter-children-div"] ul li input[type="checkbox"]:checked',
    )
    .forEach(function (input) {
      const id = input.getAttribute("data-id");
      if (id) ids.push(id);
    });

  const toggle = document.querySelector(
    '#existingUser2, .existing-user-div > button',
  );

  const activeTag = document.querySelector(
    '[data-div-type="selected-filters"] li[data-type="tag"]',
  );

  return {
    filters: ids,
    existingUser: !!toggle && toggle.getAttribute("data-state") === "active",
    tags: activeTag ? [String(activeTag.getAttribute("data-fs") || "").trim()] : [],
  };
}

/** Build the query the route takes. Shared by both modes. */
function buildQuery(parent, offset, state) {
  const parts = [
    "slug=" + encodeURIComponent(parent.getAttribute("data-slug") || ""),
    "offset=" + offset,
    "isMerchant=" + (parent.getAttribute("data-category-type") === "Merchant"),
  ];
  if (state.filters.length)
    parts.push("filters=" + encodeURIComponent(state.filters.join(",")));
  if (state.existingUser) parts.push("existingUser=true");
  if (state.tags.length) parts.push("tags=" + encodeURIComponent(state.tags.join(",")));
  return parts.join("&");
}

/** Fetch one window of the list and hand back the parsed wrapper element. */
function fetchWindow(parent, offset, state) {
  return request
    .get(webOrigin() + "/api/store/coupons?" + buildQuery(parent, offset, state), {
      throwErrors: true,
    })
    .then(function (html) {
      if (!html) throw new Error("empty response");
      // Parse before touching the page: a malformed fragment must leave the
      // list it was going to change exactly as it was.
      const holder = document.createElement("div");
      holder.innerHTML = html;
      const list = holder.firstElementChild;
      if (!list) throw new Error("no wrapper in response");
      return list;
    });
}

/**
 * Write the tab labels from the counts stored on the wrapper.
 *
 * THE COUNTS ARE READ, NEVER RECOUNTED. .in's _updateGmTabCounts counts the
 * `.gc-box` nodes on the page, which is right for .in because .in renders every
 * coupon — its node count IS the total. This page renders 80 of up to ~3,300,
 * so counting nodes reports the window instead: a single tab click turned
 * "All (3,286)" into "All (80)". Only a filter fetch changes what these mean,
 * and that path rewrites the attributes before calling this.
 */
export function syncTabCounts() {
  const parent = document.querySelector(PARENT);
  if (!parent) return;

  const counts = {
    all: toInt(parent.getAttribute("data-count-all")),
    cpn: toInt(parent.getAttribute("data-count-coupons")),
    dl: toInt(parent.getAttribute("data-count-offers")),
    fresh: toInt(parent.getAttribute("data-count-fresh")),
  };
  document
    .querySelectorAll("#gmTab > li.go-gmt-link, #gmTabMobile > li.go-gmt-link")
    .forEach(function (li) {
      const type = li.getAttribute("data-type");
      const span = li.querySelector("span");
      if (!span || !(type in counts)) return;
      span.textContent = "(" + counts[type].toLocaleString("en-US") + ")";
      // A tab the filter emptied is hidden rather than left reading (0).
      li.style.display = counts[type] === 0 && type !== "all" ? "none" : "";
    });
}

/** Copy the result-set counts off a fetched wrapper onto the page's wrapper. */
function storeTabCounts(parent, list) {
  [
    ["data-count-all", "data-count-all"],
    ["data-count-coupons", "data-count-coupons"],
    ["data-count-offers", "data-count-offers"],
    ["data-count-fresh", "data-count-fresh"],
  ].forEach(function (pair) {
    const value = list.getAttribute(pair[1]);
    if (value !== null) parent.setAttribute(pair[0], value);
  });
}

/**
 * The counter under the label. Kept as a formatted "(N left)" so the button
 * reads the way the International page's does.
 */
function setRemaining(button$, remaining) {
  const span$ = button$.find('[data-type="remaining"]');
  if (!span$.length) return;
  if (remaining > 0) {
    span$.text("(" + Number(remaining).toLocaleString("en-US") + " left)");
    return;
  }
  span$.text("");
}

/**
 * Put each section heading back between the right two coupon cards.
 *
 * The generated page is [list 1-4] [h2] [list 5-N], so the heading is a sibling
 * OF the lists, not inside one. Replacing the lists therefore strands it: the
 * old lists are removed and the new one is inserted before the button, which
 * leaves the h2 sitting above the whole list instead of after card 4. That is
 * why a heading reappears in the wrong place once a filter is cleared.
 *
 * `data-pos` names the last card that belongs above the heading, so the anchor
 * is the card with that `data-cpnpstn` and the heading goes immediately after
 * it. When the current result set is shorter than that position the anchor does
 * not exist, and the heading is left alone rather than guessed at — it is
 * hidden by the filter classes anyway.
 */
export function repositionHeadings() {
  const parent = document.querySelector(PARENT);
  if (!parent) return;

  parent.querySelectorAll("h2.gmc-title[data-pos]").forEach(function (heading) {
    const pos = heading.getAttribute("data-pos");
    if (!pos) return;
    const anchor = parent.querySelector('.gc-box[data-cpnpstn="' + pos + '"]');
    if (!anchor || !anchor.parentNode) return;
    // Already in place? Leave the DOM alone — moving a node re-triggers the
    // MutationObserver in index.js for nothing.
    if (anchor.nextElementSibling === heading) return;
    anchor.parentNode.insertBefore(heading, anchor.nextSibling);
  });
}

/**
 * Re-fetch the whole list for the current selections and replace what is on the
 * page — what a sidebar filter, the Existing User toggle or a tag chip does.
 *
 * THIS IS A SERVER CALL, NOT A CLIENT-SIDE HIDE, and it has to be. The page
 * holds 80 of up to ~2,000 coupons, so hiding non-matching cards filters the
 * window rather than the list: ticking "Home Appliances" on
 * /electronics-coupons/ should surface 259 coupons, and the ones already on the
 * page are whatever happened to be in the first 80. The International app makes
 * the same call for the same reason (`loadFilteredCoupons`), and like it, this
 * resets the list to offset 0 and the active tab to All.
 *
 * @param {(list: Element) => void} [onReplace] runs after the DOM is swapped
 */
export function applyServerFilters(onReplace) {
  const parent = document.querySelector(PARENT);
  if (!parent) return Promise.resolve();

  parent.setAttribute("data-state", "loading");

  // LAST REQUEST WINS. Ticking three boxes quickly fires three fetches, and
  // they can land out of order — without this the page could settle on the
  // result for one checkbox while three are ticked. Each call takes a token
  // and only the newest is allowed to touch the DOM.
  const token = ++filterRequestToken;

  return fetchWindow(parent, 0, currentFilterState())
    .then(function (list) {
      if (token !== filterRequestToken) return;
      // Every generated list AND every batch appended since. The expired list
      // is deliberately excluded: it is not part of the filtered result, and
      // the heading above it is hidden by toggleExpiredCouponsHeading.
      parent
        .querySelectorAll('.gmc-list:not([data-type="expired-coupons"])')
        .forEach(function (old) {
          old.parentNode.removeChild(old);
        });

      const button = parent.querySelector(BUTTON);
      if (button) parent.insertBefore(list, button);
      else parent.appendChild(list);

      const total = toInt(list.getAttribute("data-total"));
      const loaded = list.querySelectorAll(".gc-box").length;
      parent.setAttribute("data-total", String(total));
      parent.setAttribute("data-loaded", String(loaded));
      parent.setAttribute("data-next-offset", list.getAttribute("data-next-offset"));

      // The heading is a sibling of the lists, so it survived the swap above
      // and is now stranded at the top. Re-anchor it before anything else
      // reads the DOM.
      repositionHeadings();

      // A filter fetch is the ONLY thing that changes what a tab counts: the
      // result set itself is different now. A tab click is not — it re-slices
      // this same set.
      storeTabCounts(parent, list);
      syncTabCounts();

      // Back to All, exactly as the International page does after a filter
      // fetch: the tab the visitor was on may hold nothing in the new result.
      const tabs = document.querySelectorAll(
        "#gmTab > li.go-gmt-link, #gmTabMobile > li.go-gmt-link",
      );
      tabs.forEach(function (li) {
        if (li.getAttribute("data-type") === "all") li.classList.add("active");
        else li.classList.remove("active");
      });

      if (button) {
        if (list.getAttribute("data-has-more") === "True") {
          const remaining = total - loaded;
          const span = button.querySelector('[data-type="remaining"]');
          if (span)
            span.textContent =
              remaining > 0 ? "(" + remaining.toLocaleString("en-US") + " left)" : "";
          button.style.display = "";
        } else {
          // Hidden, not removed: the next filter change may have more pages,
          // and a removed button cannot come back.
          button.style.display = "none";
        }
      }

      parent.setAttribute("data-state", "idle");
      if (typeof onReplace === "function") onReplace(list);
    })
    .catch(function () {
      if (token !== filterRequestToken) return;
      // Leave the list that is on screen alone — it is still a valid view of
      // the merchant, just not of the filter that failed.
      parent.setAttribute("data-state", "error");
    });
}

export default function initCouponPagination(onAppend) {
  const parent = document.querySelector(PARENT);
  if (!parent) return;

  const button$ = $(parent).find(BUTTON);
  if (!button$.length) return;

  const label$ = button$.find("[data-show-more-label]");
  const idleLabel = label$.length ? label$.text() : "";
  let busy = false;

  button$.on("click", function () {
    // Guard on the flag, not on `disabled`: a double-click lands both events
    // before the attribute is painted, and the second would fetch the same
    // offset and append every card twice.
    if (busy) return;

    const total = toInt(parent.getAttribute("data-total"));
    const offset = toInt(parent.getAttribute("data-next-offset"));
    const slug = parent.getAttribute("data-slug");
    if (!slug || offset >= total) {
      button$.remove();
      return;
    }

    busy = true;
    button$.attr("disabled", "disabled").attr("data-state", "loading");
    if (label$.length) label$.text("Loading...");

    // The SAME selections the visitor filtered by. Without them, page two of a
    // filtered list comes back unfiltered and the button appends coupons the
    // filter had just excluded.
    fetchWindow(parent, offset, currentFilterState())
      .then(function (list) {
        const appended = list.querySelectorAll(".gc-box").length;
        if (!appended) throw new Error("no coupons in response");

        parent.insertBefore(list, button$.get(0));
        // Appended positions continue past the heading's anchor, so this
        // normally finds nothing to do — it is here so an append after a
        // filter cycle cannot leave the heading stranded.
        repositionHeadings();
        parent.setAttribute("data-next-offset", list.getAttribute("data-next-offset"));
        parent.setAttribute(
          "data-loaded",
          String(toInt(parent.getAttribute("data-loaded")) + appended),
        );

        const hasMore = list.getAttribute("data-has-more") === "True";
        if (!hasMore) {
          button$.remove();
        } else {
          setRemaining(
            button$,
            toInt(list.getAttribute("data-total")) -
              toInt(parent.getAttribute("data-loaded")),
          );
        }

        // Re-apply whatever the visitor already had selected. Appended cards
        // arrive unfiltered, so without this a batch loaded while the "Coupons"
        // tab is active would show its offers too.
        if (typeof onAppend === "function") onAppend(list);
      })
      .catch(function () {
        // The page is intact and the button still works — say so and let them
        // try again rather than leaving a dead "Loading...".
        if (label$.length) label$.text("Try again");
        button$.attr("data-state", "error");
      })
      .then(function () {
        busy = false;
        if (button$.get(0) && button$.attr("data-state") !== "error") {
          if (label$.length) label$.text(idleLabel);
          button$.attr("data-state", "idle");
        }
        button$.removeAttr("disabled");
      });
  });
}
